import { AuthModule, OidcConfigService } from 'angular-auth-oidc-client';

import { CommonModule, DecimalPipe } from '@angular/common';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import {
    APP_INITIALIZER, ErrorHandler, LOCALE_ID, NgModule, Optional, SkipSelf
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MAT_RADIO_DEFAULT_OPTIONS, MatRadioModule } from '@angular/material/radio';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatToolbarModule } from '@angular/material/toolbar';
import { RouterModule } from '@angular/router';
import { EffectsModule } from '@ngrx/effects';
import {
    DefaultRouterStateSerializer, RouterStateSerializer, StoreRouterConnectingModule
} from '@ngrx/router-store';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';

import { environment } from '../../environments/environment';
import { AuthEffects } from './auth/auth.effects';
import { AuthState } from './auth/auth.reducer';
import { configureAuth } from './auth/auth.service';
import { ConfirmDialogComponent } from './confirm/confirm-dialog.component';
import { metaReducers, reducers } from './core.state';
import {
    CertificationTypeEffects
} from './entities/certification-type/store/certification-type.effects';
import {
    CheckRequirementsEffects
} from './entities/check-requirements/store/check-requirements.effects';
import {
    ClinicalRecordTypeEffects
} from './entities/clinical-record-type/store/clinical-record-type.effects';
import { CountryCardEffects } from './entities/country-card/store/country-card.effects';
import { CountriesEffects } from './entities/country/store/country.effects';
import {
    DocumentNotificationsEffects
} from './entities/document-notifications/store/document-notifications.effects';
import { LanguageEffects } from './entities/language/store/language.effects';
import {
    PublicHealthRecordTypeEffects
} from './entities/public-health-record-type/store/public-health-record-type.effects';
import { UsersEffects } from './entities/user/store/user.effects';
import { AppErrorHandler } from './error-handler/app-error-handler.service';
import { HeaderComponent } from './header/header.component';
import { httpInterceptorProviders } from './http-interceptors';
import {
    InformationAgreementEmployeeEnComponent
} from './layout/information-agreement/information-agreement-employee/en/information-agreement-employee-en.component';
import {
    InformationAgreementEmployeeComponent
} from './layout/information-agreement/information-agreement-employee/information-agreement-employee.component';
import {
    InformationAgreementFreelanceEnComponent
} from './layout/information-agreement/information-agreement-freelance/en/information-agreement-freelance-en.component';
import {
    InformationAgreementFreelanceComponent
} from './layout/information-agreement/information-agreement-freelance/information-agreement-freelance.component';
import {
    InformationAgreementComponent
} from './layout/information-agreement/information-agreement.component';
import { LayoutComponent } from './layout/layout.component';
import { LoaderEffects } from './loader/loader.effects';
import { NotificationService } from './notifications/notification.service';
import { CustomSerializer } from './router/custom-serializer';
import { SidebarComponent } from './sidebar/sidebar.component';
import { TitleService } from './title/title.service';

// export function initializeApp(store: Store<AppState>) {
//   return (): Promise<any> => {
//     return store.pipe(select(getSelectedLanguageCode)).toPromise();
//   };
// }

export function getCurrentLocale() {
  const JSONAuth = window.localStorage.getItem('auth');
  if (!JSONAuth) {
    return 'it';
  }
  const authObj = JSON.parse(JSONAuth) as AuthState;
  return authObj.selectedLanguageCode;
}

@NgModule({
  imports: [
    // angular
    CommonModule,
    HttpClientModule,
    RouterModule,

    // ngrx
    StoreModule.forRoot(reducers, { metaReducers, runtimeChecks: { strictStateImmutability: true, strictActionImmutability: true } }),
    StoreRouterConnectingModule.forRoot({ serializer: DefaultRouterStateSerializer }),
    EffectsModule.forRoot([AuthEffects, LoaderEffects, LanguageEffects,
      CertificationTypeEffects, PublicHealthRecordTypeEffects, ClinicalRecordTypeEffects, UsersEffects,
      CheckRequirementsEffects, CountriesEffects, CountryCardEffects, DocumentNotificationsEffects]),
    environment.production
      ? []
      :
      StoreDevtoolsModule.instrument({
        name: 'e Job',
        maxAge: 25,
      }),

    // 3rd party
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    }),
    MatDialogModule,
    MatButtonModule,
    MatSnackBarModule,
    MatSidenavModule,
    MatToolbarModule,
    MatIconModule,
    MatListModule,
    MatDividerModule,
    MatMenuModule,
    MatBadgeModule,
    MatRadioModule,
    FormsModule,
    // OAuthModule.forRoot({
    //   resourceServer: {
    //     allowedUrls: ['/api'],
    //     sendAccessToken: true
    //   }
    // }),
    AuthModule.forRoot(),
  ],
  declarations: [
    ConfirmDialogComponent,
    LayoutComponent,
    HeaderComponent,
    SidebarComponent,
    InformationAgreementComponent,
    InformationAgreementEmployeeComponent,
    InformationAgreementEmployeeEnComponent,
    InformationAgreementFreelanceComponent,
    InformationAgreementFreelanceEnComponent
  ],
  entryComponents: [
    ConfirmDialogComponent
  ],
  providers: [
    NotificationService,
    httpInterceptorProviders,
    TitleService,
    DecimalPipe,
    {
      provide: LOCALE_ID,
      useFactory: getCurrentLocale
    },
    OidcConfigService,
    {
      provide: APP_INITIALIZER,
      useFactory: configureAuth,
      deps: [OidcConfigService],
      multi: true,
    },
    {
      provide: MAT_RADIO_DEFAULT_OPTIONS,
      useValue: { color: 'primary' },
    },
    { provide: ErrorHandler, useClass: AppErrorHandler },
    { provide: RouterStateSerializer, useClass: CustomSerializer }
  ],
  exports: [
    LayoutComponent
  ]
})
export class CoreModule {
  constructor(
    @Optional()
    @SkipSelf()
    parentModule: CoreModule
  ) {
    if (parentModule) {
      throw new Error('CoreModule is already loaded. Import only in AppModule');
    }
  }
}

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(
    http,
    `${environment.i18nPrefix}/assets/i18n/`,
    '.json'
  );
}
